import {
  PriceVariantFragment,
  SubscriptionPeriodFragment,
  SubscriptionPeriodPricingFragment,
} from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { PriceVariant } from '@/integrations/crystallize/types';
import { CurrencyCode } from './types/CurrencyCode';
import { CurrencySymbol } from './types/CurrencySymbol';
import { CurrencyMap } from './types/CurrencyMap';

export type { CurrencyCode, CurrencySymbol, CurrencyMap };

export function getCurrencySymbolFromCode(code: CurrencyCode): CurrencySymbol {
  return CurrencyMap[code];
}

const MISSING_PRICE = 'Missing price';
const MISSING_CURRENCY = 'Missing currency';

function missingPriceOrCurrency(actualValue: number | null, currencyCode: string | undefined) {
  const missingPrice = actualValue === null ? MISSING_PRICE : '';
  const missingCurrency = currencyCode === undefined ? MISSING_CURRENCY : '';
  return `${missingPrice} ${missingCurrency}`.trim();
}

export function getFormattedPrice(
  locale: string,
  value?: number | PriceVariant | PriceVariantFragment | null,
  options?: Intl.NumberFormatOptions,
): string {
  let actualValue: number | null = null;
  let currencyCode: string | undefined = options?.currency;

  if (typeof value === 'number') {
    actualValue = value;
    currencyCode = options?.currency; // Expect currency to be set in options if direct number is passed
  } else if (value && typeof value === 'object') {
    actualValue = value.price ?? null;
    currencyCode = value.currency || options?.currency;
  }

  if (actualValue === null || currencyCode === undefined) {
    return missingPriceOrCurrency(actualValue, currencyCode); // Return missing price if no valid value or missing currency
  }

  const priceHasDecimals = !Number.isInteger(actualValue);

  const defaultOptions: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: currencyCode,
    currencyDisplay: 'symbol', // Default to use currency symbol
    minimumFractionDigits: 2, // Default to 2 decimal places if not specified
    maximumFractionDigits: 2,
    ...options,
    // upcoming enforces the minimumFractionDigits and maximumFractionDigits to be 2
    // though developers intended it to be less than 2. It's matter of securely showing
    // proper fractional digits/prices to the user being prioritized over intention.
    ...(priceHasDecimals && { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
  };

  const formatter = new Intl.NumberFormat(locale, defaultOptions);
  return formatter.format(actualValue);
}

export function getPriceOrInitialPeriodPrice(
  priceVariant?: PriceVariant,
  subscriptionPlanPeriod?: SubscriptionPeriodFragment,
) {
  return priceVariant?.price || subscriptionPlanPeriod?.initial?.price;
}

export function getFormattedPriceForSubscriptionPeriod(
  locale: string,
  subscriptionPlanPeriodPricing?: SubscriptionPeriodPricingFragment | null,
  options?: Intl.NumberFormatOptions,
) {
  if (!subscriptionPlanPeriodPricing) {
    return MISSING_PRICE;
  }
  return `${getFormattedPrice(locale, subscriptionPlanPeriodPricing.price, options)}
   / ${subscriptionPlanPeriodPricing.period > 1 ? subscriptionPlanPeriodPricing.period : ''}
    ${subscriptionPlanPeriodPricing.unit}`; // TODO unit (month, year..) string translation
}
